import { Component, OnInit, OnDestroy } from "@angular/core";
import { SwUpdate } from "@angular/service-worker";
import { environment } from "../environments/environment";
import { Router, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import { CallStateService } from "./services/call-state.service";
import { Subscription } from "rxjs";
import { AgentType } from "../../../interfaces";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  title = "Demo App";

  // PWA installation properties
  deferredPrompt: any;
  showInstallButton = false;
  installationLog: string[] = [];
  showDebugPanel = false; // Debug panel is hidden by default

  // Route tracking
  isCallInProgressRoute = false;
  isCallTranscriptRoute = false;

  // Install button properties
  installPrompt: any;
  installationStatus: string = "";

  // Agent name from the service
  agentName: AgentType | null = null;
  private subscription = new Subscription();

  constructor(
    private swUpdate: SwUpdate,
    private router: Router,
    private callStateService: CallStateService,
  ) {}

  ngOnInit(): void {
    // Subscribe to agent name changes
    this.subscription.add(
      this.callStateService.agentName$.subscribe((name) => {
        this.agentName = name;
      }),
    );

    // Track route changes to detect when we're on the call-in-progress page
    this.subscription.add(
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd,
          ),
        )
        .subscribe((event: NavigationEnd) => {
          this.isCallInProgressRoute = event.url.includes("call-in-progress");
          this.isCallTranscriptRoute = event.url.includes("call-transcript");
        }),
    );

    // Clear any flags that might prevent PWA installation
    localStorage.removeItem("pwa_sw_unregistered");
    this.logInstallationStatus("Cleared PWA flags from localStorage");

    // Add guard to prevent multiple reloads in the same session
    let hasReloaded = sessionStorage.getItem("sw_version_reloaded") === "true";

    if (environment.enableServiceWorker) {
      if (this.swUpdate.isEnabled) {
        this.swUpdate.versionUpdates.subscribe((event) => {
          if (event.type === "VERSION_READY") {
            if (!hasReloaded) {
              hasReloaded = true;
              sessionStorage.setItem("sw_version_reloaded", "true");
              if (
                confirm(
                  "A new version of the demo app is available. Load the new version?",
                )
              ) {
                window.location.reload();
              }
            } else {
              this.logInstallationStatus(
                "Version update detected but reload already performed this session",
              );
            }
          }
        });
        this.logInstallationStatus("Service worker is enabled");
      } else {
        this.logInstallationStatus("Service worker is NOT enabled");
      }
    } else {
      this.logInstallationStatus("Service worker is disabled in environment");
    }

    // Listen for the beforeinstallprompt event
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault(); // Prevent the default install prompt

      // Save the event for later use
      this.installPrompt = event;

      this.installationStatus = "App can be installed";
    });

    // Add a keyboard shortcut to toggle the debug panel (Ctrl+Shift+D)
    window.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        this.showDebugPanel = !this.showDebugPanel;
      }
    });

    // Listen for the appinstalled event
    window.addEventListener("appinstalled", () => {
      this.logInstallationStatus("App was installed successfully");
      this.installationStatus = "App installed successfully";
      this.installPrompt = null;
    });

    // Check if the app is already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      this.logInstallationStatus(
        "App is already installed and running in standalone mode",
      );
      this.installationStatus = "App is already installed";
      this.installPrompt = null; // Set installPrompt to null when in standalone mode
    }
  }

  // Method to toggle debug panel visibility
  toggleDebugPanel(): void {
    this.showDebugPanel = !this.showDebugPanel;
  }

  // Method to log installation status for debugging
  logInstallationStatus(message: string): void {
    this.installationLog.push(`${new Date().toLocaleTimeString()}: ${message}`);
  }

  // Method to clear browser cache and storage
  clearBrowserCache(): void {
    this.logInstallationStatus(
      "Attempting to clear browser cache and storage...",
    );

    // Clear localStorage items related to PWA
    localStorage.removeItem("pwa_sw_unregistered");

    // Clear application cache
    if ("caches" in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName).then(() => {
            this.logInstallationStatus(`Deleted cache: ${cacheName}`);
          });
        });
      });
    }

    // Clear IndexedDB databases
    if (window.indexedDB) {
      window.indexedDB
        .databases()
        .then((databases) => {
          databases.forEach((database) => {
            if (database.name) {
              window.indexedDB.deleteDatabase(database.name);
              this.logInstallationStatus(
                `Deleted IndexedDB database: ${database.name}`,
              );
            }
          });
        })
        .catch((error) => {
          this.logInstallationStatus(
            `Error listing IndexedDB databases: ${error}`,
          );
        });
    }

    this.logInstallationStatus(
      "Browser cache clearing complete. Please refresh the page.",
    );
  }

  // Method to check PWA installation criteria
  checkInstallationCriteria(): void {
    // Check if running on HTTPS or localhost
    const isSecure =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    this.logInstallationStatus(`Secure context: ${isSecure ? "Yes" : "No"}`);

    // Check if service worker is registered
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        this.logInstallationStatus(
          `Service worker registrations: ${registrations.length}`,
        );

        registrations.forEach((registration) => {
          this.logInstallationStatus(
            `Service worker scope: ${registration.scope}`,
          );
        });
      });
    } else {
      this.logInstallationStatus(
        "Service worker API not available in this browser",
      );
    }

    // Check manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      this.logInstallationStatus(
        `Manifest found: ${manifestLink.getAttribute("href")}`,
      );
    } else {
      this.logInstallationStatus("No manifest link found in the document");
    }

    // Check if already in standalone mode
    if (window.matchMedia("(display-mode: standalone)").matches) {
      this.logInstallationStatus("App is already running in standalone mode");
    }
  }

  // Method to check for and optionally unregister service worker registrations
  // This is now only used for debugging purposes and not called automatically
  checkAndUnregisterServiceWorkers(): void {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        if (registrations.length > 0) {
          this.logInstallationStatus(
            `Found ${registrations.length} service worker registrations`,
          );

          // Ask for confirmation before unregistering
          if (
            confirm(
              "Do you want to unregister all service workers? This will disable PWA functionality until the page is reloaded.",
            )
          ) {
            // Unregister all service workers
            registrations.forEach((registration) => {
              registration.unregister().then((success) => {
                if (success) {
                  this.logInstallationStatus(
                    `Successfully unregistered service worker for: ${registration.scope}`,
                  );
                } else {
                  this.logInstallationStatus(
                    `Failed to unregister service worker for: ${registration.scope}`,
                  );
                }
              });
            });

            // Clear application cache
            if ("caches" in window) {
              caches.keys().then((cacheNames) => {
                cacheNames.forEach((cacheName) => {
                  if (cacheName.includes("ngsw")) {
                    caches.delete(cacheName).then(() => {
                      this.logInstallationStatus(`Deleted cache: ${cacheName}`);
                    });
                  }
                });
              });
            }

            // Remove the localStorage flag to allow re-registration
            localStorage.removeItem("pwa_sw_unregistered");

            this.logInstallationStatus(
              "Service workers unregistered and caches cleared. Refresh the page to see changes.",
            );
          }
        } else {
          this.logInstallationStatus("No service worker registrations found");
        }
      });
    }
  }

  // Method to force service worker registration
  registerServiceWorker(): void {
    // Guard: do nothing if disabled
    if (!environment.enableServiceWorker) {
      return;
      this.logInstallationStatus(
        "Service worker registration skipped - disabled in environment",
      );
    }

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        if (registrations.length === 0) {
          this.logInstallationStatus(
            "No service worker registered, attempting to register manually",
          );

          navigator.serviceWorker
            .register("/ngsw-worker.js")
            .then((registration) => {
              this.logInstallationStatus(
                `Service worker registered successfully with scope: ${registration.scope}`,
              );
            })
            .catch((error) => {
              this.logInstallationStatus(
                `Service worker registration failed: ${error}`,
              );
            });
        } else {
          this.logInstallationStatus(
            `Service worker already registered (${registrations.length} registrations found)`,
          );
        }
      });
    }
  }

  // Method to handle app installation
  installApp(): void {
    if (!this.installPrompt) {
      // Provide fallback instructions based on browser
      const userAgent = navigator.userAgent.toLowerCase();
      let message = "";

      if (userAgent.includes("chrome")) {
        message =
          'In Chrome: Click the menu button (⋮) and select "Install App"';
      } else if (userAgent.includes("firefox")) {
        message =
          'In Firefox: Click the menu button (≡) and look for "Install" option';
      } else if (userAgent.includes("safari")) {
        message =
          'In Safari: Click the share button and select "Add to Home Screen"';
      } else {
        message = "Use your browser's menu to install this app to your device";
      }

      this.installationStatus = message;
      return;
    }

    // Show the install prompt
    this.installPrompt.prompt();

    // Wait for the user to respond to the prompt
    this.installPrompt.userChoice
      .then((choiceResult: { outcome: string }) => {
        if (choiceResult.outcome === "accepted") {
          this.installationStatus = "Installation accepted";
        } else {
          this.installationStatus = "Installation dismissed";
        }
        // Clear the saved prompt
        this.installPrompt = null;
      })
      .catch((error: unknown) => {
        this.installationStatus = "Installation error occurred";
      });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscription.unsubscribe();
  }
}
